(() => {
  if (customElements.get('rtb-photo-upload')) return;
  // Use Dawn's existing product-info renderer so price, stock, URL and quantity rules stay native.
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-photo-variant]');
    if (!select) return;
    event.stopImmediatePropagation();
    const info = select.closest('product-info');
    if (!info) return;
    const photo = info.querySelector('rtb-photo-upload');
    if (photo) { photo.variantPending=true; photo.invalidate(); photo.say('Loading selected block size… If this does not finish, select the size again.'); }
    info.resetProductFormState();
    info.renderProductInfo({
      requestUrl: `${info.dataset.url}?variant=${encodeURIComponent(select.value)}&section_id=${encodeURIComponent(info.dataset.section)}`,
      targetId: select.id,
      callback: (html) => {
        const sourceButton=html.querySelector('[id^="ProductSubmitButton-"]');
        if (sourceButton?.hasAttribute('data-photo-upload-gated')) sourceButton.removeAttribute('disabled');
        if (photo) photo.nativeDisabled=!sourceButton || sourceButton.hasAttribute('disabled');
        info.handleUpdateProductInfo(info.dataset.url)(html);
        if (photo) {
          photo.variantPending=false;
          if (!photo.image && photo.status.textContent === 'Loading selected block size… If this does not finish, select the size again.') photo.say('Choose a photo to begin.');
          photo.gate();
        }
      }
    });
  }, true);

  customElements.define('rtb-photo-upload', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.closest('form');
      this.variants = JSON.parse(this.querySelector('[data-photo-variants]').textContent);
      this.fileInput = this.querySelector('[data-photo-file]');
      this.status = this.querySelector('[data-photo-status]');
      this.canvas = this.querySelector('canvas');
      this.approve = this.querySelector('[data-photo-approve]');
      this.fit = this.querySelector('[data-photo-fit]');
      this.x = this.querySelector('[data-photo-x]');
      this.y = this.querySelector('[data-photo-y]');
      this.epoch = 0;
      this.uploadEpoch = 0;
      this.fileInput.addEventListener('change', () => this.chooseFile());
      [this.fit, this.x, this.y].forEach(input => input.addEventListener('input', () => { this.invalidate(); this.draw(); }));
      this.form.querySelector('[name=id]').addEventListener('change', () => { this.invalidate(); this.draw(); });
      this.approve.addEventListener('click', () => this.prepare());
      this.submit = this.form.querySelector('[type=submit]');
      this.nativeDisabled = this.submit.disabled && !this.submit.hasAttribute('data-photo-upload-gated');
      this.buttonObserver = new MutationObserver(() => this.gate());
      this.buttonObserver.observe(this.submit, {attributes:true, attributeFilter:['disabled']});
      this.draw(); this.gate();
    }
    disconnectedCallback() { this.buttonObserver?.disconnect(); this.controller?.abort(); if (this.objectURL) URL.revokeObjectURL(this.objectURL); }
    gate() {
      if (!this.submit) return;
      this.approve.disabled = !!this.preparing || !!this.variantPending || !this.originalURL;
      const disabled = this.nativeDisabled || !this.readyForCart() || !this.variant()?.available;
      if (this.submit.disabled !== disabled) this.submit.disabled = disabled;
      if (!this.nativeDisabled && this.variant()?.available && this.submit.querySelector('span')) {
        this.submit.querySelector('span').textContent = this.readyForCart() ? window.variantStrings.addToCart : 'Upload and approve photo';
      }
    }
    variant() { return this.variants.find(v => String(v.id) === this.form.querySelector('[name=id]').value); }
    dimensions() {
      const variant = this.variant();
      if (!variant) return null;
      const sizes = String(variant.option1).match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i);
      if (!sizes) return null;
      let w = Number(sizes[1]), h = Number(sizes[2]);
      if (/landscape/i.test(variant.option2) && w < h) [w,h] = [h,w];
      return { w, h, orientation:w === h ? 'Square' : w > h ? 'Landscape' : 'Portrait', id:String(variant.id) };
    }
    say(message) { this.status.textContent = message; }
    invalidate() {
      this.epoch++;
      this.ready = null;
      this.querySelectorAll('[data-photo-property]').forEach(input => input.remove());
      if (this.image) this.say('Check the size and crop, then approve the preview.');
      this.approve.disabled = !!this.preparing || !this.originalURL;
      this.gate();
    }
    property(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value;
      input.dataset.photoProperty = ''; this.append(input);
    }
    async upload(file, signal) {
      const fd = new FormData(); fd.append('file', file, file.name);
      const requestController = new AbortController();
      const cancel = () => requestController.abort();
      signal?.addEventListener('abort', cancel, {once:true});
      if (signal?.aborted) cancel();
      const timer = setTimeout(cancel, 90000);
      try {
        const response = await fetch(this.dataset.worker, { method:'POST', body:fd, signal:requestController.signal });
        if (!response.ok) throw new Error(`Upload failed (${response.status}). Please try again.`);
        const data = await response.json();
        if (data.error || !data.url) throw new Error('Upload did not return a usable file. Please try again.');
        const url = new URL(data.url);
        if (url.protocol !== 'https:') throw new Error('Upload returned an invalid link. Please try again.');
        return url.href;
      } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
    }

    async chooseFile() {
      const token = ++this.uploadEpoch;
      this.controller?.abort(); this.controller = new AbortController();
      this.invalidate(); this.originalURL = null; this.image = null;
      this.querySelector('[data-photo-editor]').hidden = true;
      this.approve.disabled = true;
      if (this.objectURL) URL.revokeObjectURL(this.objectURL);
      const file = this.fileInput.files[0];
      this.fileInput.value = '';
      if (!file) { this.say('Choose a photo to begin.'); return; }
      if (!/\.(jpe?g|png|webp)$/i.test(file.name) || (file.type && !/^image\/(jpeg|png|webp)$/.test(file.type))) {
        this.say('Please choose a JPG, PNG or WebP. Export HEIC/HEIF as JPG first.'); return;
      }
      if (!file.size || file.size > 25 * 1024 * 1024) { this.say('Choose a photo smaller than 25 MB and not empty.'); return; }
      this.say('Reading photo…');
      try {
        const image = new Image(); this.objectURL = URL.createObjectURL(file);
        image.src = this.objectURL; await image.decode();
        if (token !== this.uploadEpoch) return;
        if (!image.naturalWidth || image.naturalWidth * image.naturalHeight > 40000000) throw new Error('Photo is too large to preview safely. Export a JPG under 40 megapixels.');
        this.file = file; this.image = image; this.x.value = this.y.value = '50';
        this.querySelector('[data-photo-editor]').hidden = false; this.draw();
        this.say('Uploading original photo… Please keep this page open.');
        const originalURL = await this.upload(file, this.controller.signal);
        if (token !== this.uploadEpoch) return;
        this.originalURL = originalURL; this.approve.disabled = false;
        this.say('Photo uploaded. Check the size and crop, then approve the preview.');
      } catch (error) {
        if (token !== this.uploadEpoch) return;
        this.originalURL = null; this.approve.disabled = true;
        this.say(error.name === 'AbortError' ? 'Upload cancelled. Choose the photo again.' : `Photo not ready: ${error.message || 'Could not read this image. Export it as JPG and try again.'}`);
      }
    }
    geometry(d) {
      const iw=this.image.naturalWidth, ih=this.image.naturalHeight, ratio=d.w/d.h;
      let sx=0,sy=0,sw=iw,sh=ih,dx=0,dy=0,dw=d.w*300,dh=d.h*300;
      if (this.fit.value === 'fill') {
        if (iw/ih > ratio) sw=ih*ratio; else sh=iw/ratio;
        sx=(iw-sw)*Number(this.x.value)/100; sy=(ih-sh)*Number(this.y.value)/100;
      } else {
        const scale=Math.min(dw/iw,dh/ih); const bw=dw,bh=dh;
        dw=iw*scale; dh=ih*scale; dx=(bw-dw)/2; dy=(bh-dh)/2;
      }
      return {sx,sy,sw,sh,dx,dy,dw,dh};
    }
    draw() {
      const d=this.dimensions();
      if (!d) { this.say('Please select an available block size and orientation.'); return; }
      this.querySelector('[data-photo-dimensions]').textContent=`${d.w} × ${d.h} in • ${d.orientation} • 3/4 in thick`;
      this.querySelector('[data-photo-position]').hidden=this.fit.value==='fit';
      if (!this.image) return;
      this.canvas.width=d.w*300; this.canvas.height=d.h*300;
      const ctx=this.canvas.getContext('2d'); ctx.fillStyle='#fff'; ctx.fillRect(0,0,this.canvas.width,this.canvas.height);
      const g=this.geometry(d); ctx.drawImage(this.image,g.sx,g.sy,g.sw,g.sh,g.dx,g.dy,g.dw,g.dh);
      const dpi=Math.round(Math.min(g.sw/(g.dw/300),g.sh/(g.dh/300)));
      this.querySelector('[data-photo-quality]').textContent=`Effective photo resolution: ${dpi} DPI. ${dpi < 150 ? 'Low resolution: a larger original is recommended; printing may look soft.' : dpi < 300 ? 'May look softer than a high-resolution original.' : 'Good resolution for this size.'} Output is prepared at 300 pixels per inch; this cannot add missing detail.`;
    }
    readyForCart() {
      return !!this.ready && !this.preparing && !this.variantPending && this.ready.epoch===this.epoch && this.ready.variant===String(this.variant()?.id);
    }
    async printPNG(blob) {
      // Canvas PNGs normally carry screen resolution. Write standard pHYs = 300 DPI.
      const source = new Uint8Array(await blob.arrayBuffer());
      const chunk = new Uint8Array(21); const view = new DataView(chunk.buffer);
      view.setUint32(0,9); chunk.set([112,72,89,115],4);
      view.setUint32(8,11811); view.setUint32(12,11811); chunk[16]=1;
      let crc=0xffffffff;
      for (const byte of chunk.slice(4,17)) { crc^=byte; for(let bit=0;bit<8;bit++) crc=(crc>>>1)^((crc&1)?0xedb88320:0); }
      view.setUint32(17,(crc^0xffffffff)>>>0);
      const parts=[source.slice(0,33),chunk];
      for(let pos=33;pos<source.length;) {
        const length=new DataView(source.buffer,source.byteOffset+pos,4).getUint32(0)+12;
        if (String.fromCharCode(...source.slice(pos+4,pos+8))!=='pHYs') parts.push(source.slice(pos,pos+length));
        pos+=length;
      }
      return new Blob(parts,{type:'image/png'});
    }
    async prepare() {
      if (!this.originalURL || !this.image || this.preparing || this.variantPending) return;
      this.invalidate(); this.draw();
      const d=this.dimensions(); if (!d) return;
      const epoch=this.epoch, token=this.uploadEpoch, signal=this.controller.signal;
      this.preparing=true; this.approve.disabled=true;
      this.say('Preparing and uploading your approved crop… Do not add to cart yet.');
      try {
        const blob=await new Promise((resolve,reject) => this.canvas.toBlob(b => b ? resolve(b) : reject(new Error('Could not prepare the crop.')), 'image/png'));
        if (epoch!==this.epoch || token!==this.uploadEpoch) return;
        const printBlob=await this.printPNG(blob);
        if (epoch!==this.epoch || token!==this.uploadEpoch) return;
        const cropFile=new File([printBlob],`photo-block-${d.w}x${d.h}-${crypto.randomUUID()}.png`,{type:'image/png'});
        const productionURL=await this.upload(cropFile,signal);
        if (epoch!==this.epoch || token!==this.uploadEpoch) return;
        this.property('Photo filename',this.file.name);
        this.property('Block dimensions',`${d.w} × ${d.h} in`);
        this.property('Orientation',d.orientation);
        this.property('Photo fit',this.fit.value==='fill'?'Fill block — approved crop':'Fit entire photo — white borders');
        this.property('Photo preview approved','Yes');
        this.property('_Original Artwork URL',this.originalURL);
        this.property('_Production Artwork URL',productionURL);
        this.property('_Photo Crop',JSON.stringify({version:1,variant:d.id,widthInches:d.w,heightInches:d.h,outputWidth:this.canvas.width,outputHeight:this.canvas.height,fit:this.fit.value,...this.geometry(d)}));
        this.ready={epoch,variant:d.id};
        this.say('Approved crop saved. Your photo is ready to add to cart.');
      } catch(error) {
        if (epoch===this.epoch) this.say(`Photo not ready: ${error.message || 'Upload failed. Please try approving again.'}`);
      } finally {
        this.preparing=false; this.approve.disabled=!this.originalURL; this.gate();
      }
    }
  });
})();

